import { Component, EventEmitter, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EmailEditorComponent } from 'angular-email-editor';
import { ConfirmationService } from 'primeng/api';
import { forkJoin, Observable, ReplaySubject, Subject } from 'rxjs';
import { IPendingChangesComponent } from 'src/app/guards/pending-changes.guard';
import { Campaign, EmailType } from 'src/app/models/communication/campaign';
import { Template } from 'src/app/models/communication/template';
import { Employer } from 'src/app/models/employer';
import { DataService } from 'src/app/services/data.service';
import { SiteService } from 'src/app/services/site.service';

@Component({
  selector: 'app-campaign-details',
  templateUrl: './campaign-details.component.html',
  styleUrls: ['./campaign-details.component.scss']
})
export class CampaignDetailsComponent implements IPendingChangesComponent {

	@ViewChild(EmailEditorComponent) emailEditor: EmailEditorComponent;
	
	public campaign: Campaign;
	public employerType: 'all' | 'selected';
	public allEmployers: Array<{ employerID: string, employerName: string, selected: boolean }> = []; //all employers
	public filteredEmployers: Array<{ employerID: string, employerName: string, selected: boolean }> = []; //filtered list to show in the dialog
	public selectedEmployers: Array<{ employerID: string, employerName: string, selected: boolean }> = []; //actual selected employers (keeping this separate from allemployers so you can cancel the dialog and not lose your selection)
	public employeeSearchTerm: string = "";
	public showEmployeeChooser: boolean = false;

	public emailType = EmailType;

	public templates: Array<Template> = [];
	public selectedTemplate: string;
	public editorOptions = {};
	private isNewCampaign: boolean = false;
	private gotCampaign: EventEmitter<boolean> = new EventEmitter();
	private gotEmployers: EventEmitter<boolean> = new EventEmitter();

	public testFormVisible: boolean = false;
	public testEmail: string = "";
	public testPhone: string = "";
	public testEmployer: Employer;

	public errors: any = {};
	public isDirty: boolean = false;
	public missingEmployers: any[] = [];
	public showMissingWarning: boolean = false;
	public statusId: number;

	constructor(private dataService: DataService, private route: ActivatedRoute, private router: Router, private siteService: SiteService, private confirmationService: ConfirmationService) { }

	ngOnInit() {
		forkJoin([this.gotCampaign.asObservable(), this.gotEmployers.asObservable()]).subscribe(() => {
			// For drip campaigns, force all employers
			if (this.campaign.emailTypeID === EmailType.Drip) {
				this.employerType = 'all';
				this.selectedEmployers = [];
			} else {
				this.selectedEmployers = this.allEmployers.filter((employer) => {
					return this.campaign.employers.includes(employer.employerID);
				});
				this.selectedEmployers.forEach((employer) => {
					this.allEmployers.find((e) => e.employerID === employer.employerID).selected = true;
				});
			}
		});
		this.route.paramMap.subscribe(p => {
			let id = p.get('id');
			if(id == 'new') {
				this.siteService.pageTitle = 'New Campaign';
				this.campaign = new Campaign({ campaignID: crypto.randomUUID(), campaignStatusID: 4 });
				this.isNewCampaign = true;
				this.gotCampaign.emit(true);
				this.gotCampaign.complete();
				this.statusId = 4;
				this.updateHeaderButtons();
			} else {
				this.refreshCampaign(id).then(() => {
					this.siteService.pageTitle = this.campaign.campaignName;
					this.gotCampaign.emit(true);
					this.gotCampaign.complete();
					this.statusId = this.campaign.campaignStatusID;
				});
			}
		});
		this.dataService.getEmployers().subscribe((data) => {
			this.allEmployers = data.map((employer) => {
				return { employerID: employer.employerID, employerName: employer.employerName, selected: false };
			});
			this.filteredEmployers = this.allEmployers;
			this.gotEmployers.emit(true);
			this.gotEmployers.complete();
		});
		this.dataService.getTemplates().subscribe((data) => {
			this.templates = data;
		});
		this.updateHeaderButtons();
		this.siteService.backButton = { title: 'Back to Campaigns', link: '/admin/campaigns' };

	}

	refreshCampaign(id: string) {
		return new Promise<void>((resolve) => {
			this.dataService.getCampaign(id).subscribe((data:any) => {
				let campaign = new Campaign(data);
				this.campaign = campaign;
				this.employerType = this.campaign.sendAllEmployers ? 'all' : 'selected';
				this.updateHeaderButtons();
				resolve();
			});
		});
	}
	selectEmployers() {
		// Don't allow employer selection for drip campaigns
		if (this.campaign && this.campaign.emailTypeID === EmailType.Drip) {
			return;
		}
		
		this.filteredEmployers = this.allEmployers;
		this.showEmployeeChooser = true;
	}

	filterEmployers() {
		this.filteredEmployers = this.allEmployers.filter((employer) => {
			return employer.employerName.toLowerCase().includes(this.employeeSearchTerm.toLowerCase());
		});
	}

	toggleActive() {
		if(this.statusId == 4) {
			this.statusId = this.campaign.campaignStatusID == 4 ? 1 : this.campaign.campaignStatusID;
		} else {
			this.statusId = 4;
		}
		this.isDirty = true;
	}
	setCampaignType(type: EmailType) {
		// Email type is set via the radio button binding
		// Update header buttons based on campaign type
		this.updateHeaderButtons();
		
		// For drip campaigns, force all employers
		if (type === EmailType.Drip) {
			this.employerType = 'all';
			this.selectedEmployers = [];
		}
	}
	confirm() {
		this.selectedEmployers = this.allEmployers.filter((employer) => {
			return employer.selected;
		});
		this.showEmployeeChooser = false;
		this.employerType = this.selectedEmployers.length > 0 ? 'selected' : 'all';
	}
	close() {
		this.showEmployeeChooser = false;
		this.employeeSearchTerm = "";
	}

	editorLoaded(e: any) {
		if(this.campaign.emailJSON && this.campaign.emailJSON !== '{}') {
			setTimeout(() =>  this.emailEditor.loadDesign(JSON.parse(this.campaign.emailJSON)), 1000);
		}/*
		(this.emailEditor.editor as any).registerTool({
			name: 'substitution_fields',
			label: 'Substitution Fields',
			icon: 'fa fa-tags',
			supportedDisplayModes: ['web', 'email'],
			options: {
				field: {
					title: 'Substitution Field',
					defaultValue: ''
				}
			},
			renderer: {
				Viewer: (values: any) => {
					return { html: `<span class="substitution-field">${values.field}</span>` };
				},
				exporters: {
					web: (values: any) => {
						return `<span class="substitution-field">${values.field}</span>`;
					},
					email: (values: any) => {
						return `<span class="substitution-field">${values.field}</span>`;
					}
				}
			}
		})
			*/

		this.emailEditor.editor.registerCallback('design:updated', () => {
			this.isDirty = true;
		});
	}

	markDirty(e:any) {
		this.isDirty = true;
	}
	templateSelected(e: any) {
		const template = e.value;
		if (!template || template.templateJSON == '{}') return;
		if (this.campaign.emailJSON && this.campaign.emailJSON !== '{}') {
			this.confirmationService.confirm({
				message: "This template is not empty.  By selecting a new template, you will lose any changes you have made to this template.",
				header: "Are you sure?",
				acceptLabel: "Select Template",
				rejectLabel: "Cancel",
				acceptIcon: 'check',
				rejectIcon: 'times',
				rejectButtonStyleClass: 'danger',
				accept: () => {
					this.emailEditor.loadDesign(JSON.parse(template.templateJSON));
					this.isDirty = true;
				},
				reject: () => {
					this.selectedTemplate = null;
				}
			})
		} else {
			this.emailEditor.loadDesign(JSON.parse(template.templateJSON));
			this.isDirty = true;
		}
	}

	copyToClipboard(field: string) {
		navigator.clipboard.writeText(field).then(() => {
			this.siteService.setMessage('Copied to clipboard');
		})
	}
	canDeactivate(): boolean | Observable<boolean> {
		return !this.isDirty;
	}
	async save() {
		return new Promise<boolean>(async (resolve, reject) => {
			this.errors = {};
			await new Promise<void>((resolve2) => {
				this.emailEditor.exportHtml((data: any) => {
					let html = data.html.replace(/&amp;/ig, '&');
					this.campaign.eMailContent = html;
					this.campaign.emailJSON = JSON.stringify(data.design);
					resolve2();
				});
			});
			this.campaign.sendAllEmployers = this.employerType === 'all';
			this.campaign.employers = this.selectedEmployers.map(e => e.employerID);
			this.campaign.campaignStatusID = this.statusId;
			
			// For drip campaigns, force sendAllEmployers to true
			if (this.campaign.emailTypeID === EmailType.Drip) {
				this.campaign.sendAllEmployers = true;
				this.campaign.employers = [];
			}
			let campaignToSave = new Campaign(this.campaign); //this is to prevent the deliveryDateTime from flashing on the page before save.
			this.dataService.saveCampaign(campaignToSave).subscribe((res: any) => {
				this.siteService.setMessage('Campaign saved');
				this.isNewCampaign = false;
				this.refreshCampaign(this.campaign.campaignID);
				this.isDirty = false;
				this.missingEmployers = res?.missingEmployers ?? [];
				if (this.missingEmployers.length) this.showMissingWarning = true;
				resolve(true);
			}, (err) => {
				this.errors = err.error.errors;
				reject(false);
			});
		});

	}

	delete() {
		this.confirmationService.confirm({
			message: "Are you sure you want to delete this campaign?  This action cannot be undone.",
			header: "Delete Campaign?",
			acceptLabel: "Delete Campaign",
			rejectLabel: "Cancel",
			acceptIcon: 'check',
			rejectIcon: 'times',
			rejectButtonStyleClass: 'danger',
			accept: () => {
				this.dataService.deleteCampaign(this.campaign.campaignID).subscribe(() => {
					this.isDirty = false;
					this.router.navigate(['/admin', 'campaigns']).then(() => {
						this.siteService.setMessage('Campaign deleted');
					});
				});
			}
		})
	}


	test() {
		this.testFormVisible = true;
		this.testEmail = "";
		this.testPhone = "";
		this.testEmployer = null;
	}

	sendTest() {
		if (this.testEmail && this.testEmployer) {
			this.dataService.testCampaign(this.campaign.campaignID, this.testEmail, this.testPhone, this.testEmployer.employerID).subscribe(() => {
				this.siteService.setMessage('Test communications sent');
				this.testFormVisible = false;
			});
		}
	}

	execute() {
		this.confirmationService.confirm({
			message: "Are you sure you want to execute this campaign?  The campaign will be delivered immediately.",
			header: "Execute Campaign?",
			acceptLabel: "Execute Campaign",
			rejectLabel: "Cancel",
			acceptIcon: 'check',
			rejectIcon: 'times',
			rejectButtonStyleClass: 'danger',
			accept: () => {
				this.dataService.executeCampaign(this.campaign.campaignID).subscribe(() => {
					this.siteService.setMessage('Campaign executing');
				});
			}
		})
	}

	get selectedEmployerNames() {
		let names = this.selectedEmployers.slice(0,3).map(e => e.employerName).join(", ");
		if (this.selectedEmployers.length > 3) {
			names += ` + ${this.selectedEmployers.length - 3} more`;
		}
		return names;
	}

	updateHeaderButtons() {
		const isDripCampaign = this.campaign && this.campaign.emailTypeID === EmailType.Drip;
		
		// Build actions menu - exclude Execute for drip campaigns
		const actions = [
			{ text: 'Test', callback: () => this.test() }
		];
		
		if (!isDripCampaign) {
			actions.push({ text: 'Execute', callback: () => this.execute() });
		}

		this.siteService.headerButtons = [
			{ text: 'Actions', class: 'neutral', children: actions },
			{ text: 'Save', class: 'success', callback: () => this.save() },
			{ text: 'Delete', class: 'danger', callback: () => this.delete() }
		];
	}
}
